import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";
import DataTable from "@/components/DataTable";
import EmployeePicker from "@/components/EmployeePicker";
import { Plus, X, Pencil, Trash2, History, UserPlus, KeyRound, Fuel, CheckCircle2, AlertTriangle, Archive, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import HelpHint from "@/components/HelpHint";
import BulkImportButton from "@/components/BulkImportButton";
import { useBulkSelect, BulkCheckbox, BulkActionBar } from "@/components/BulkSelect";
import FirmFilter from "@/components/FirmFilter";
import { EmptyState } from "@/components/Shared";
import { fmtDate, fmtDateTime } from "@/lib/format";

const STATUS_META = {
  dispo:    { label: "In Dispo",        color: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", Icon: Archive },
  assigned: { label: "Zugewiesen",      color: "bg-blue-500/10 text-blue-300 border-blue-500/30",         Icon: CheckCircle2 },
  zurueck:  { label: "Zurück zum Begeh", color: "bg-amber-500/10 text-amber-300 border-amber-500/30",      Icon: Archive },
  damaged:  { label: "Beschädigt",      color: "bg-orange-500/10 text-orange-300 border-orange-500/30",   Icon: AlertTriangle },
  lost:     { label: "Verloren",        color: "bg-rose-500/10 text-rose-300 border-rose-500/30",         Icon: HelpCircle },
};

const TYPE_META = {
  begeh: { label: "Begehkarten", singular: "Begehkarte", Icon: KeyRound },
  tank:  { label: "Tankkarten",  singular: "Tankkarte",  Icon: Fuel },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.dispo;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${m.color}`}>
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
}

export default function Karten() {
  const { can } = usePermissions();
  const canEdit = can("karten", "edit");
  const canCreate = can("karten", "create");
  const canDelete = can("karten", "delete");

  const [type, setType] = useState("begeh");   // "begeh" | "tank"
  const [cards, setCards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [assignModal, setAssignModal] = useState(null);  // card being assigned
  const [historyModal, setHistoryModal] = useState(null); // card whose history is open

  // Phase 5Z.25 — bulk-select + confirm dialog
  const bulk = useBulkSelect(cards);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/cards", { params: { type } });
      setCards(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const empName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : (id ? "—" : "");
  };

  async function remove(card) {
    setConfirmState({ type: "single", card });
  }
  async function confirmRemove() {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.type === "single") {
        await api.delete(`/cards/${confirmState.card.id}`);
        toast.success("Karte gelöscht");
      } else {
        const ids = bulk.selectedIds;
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/cards/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) toast.success(`${ok} Karte${ok === 1 ? "" : "n"} gelöscht`);
        if (fail) toast.error(`${fail} fehlgeschlagen`);
        bulk.clear();
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setConfirmBusy(false); setConfirmState(null); }
  }

  const typeMeta = TYPE_META[type];
  const TypeIcon = typeMeta.Icon;

  return (
    <div className="p-6 md:p-8" data-testid="karten-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100">Karten-Verwaltung</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Begehkarten + Tankkarten mit Zuweisung und vollständiger Historie</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <BulkImportButton
              entity={`cards-${type}`}
              templateUrl={`/imports/cards-${type === "begeh" ? "begeh" : "tank"}/template`}
              uploadUrl={`/imports/cards-${type === "begeh" ? "begeh" : "tank"}`}
              templateFileName={type === "begeh" ? "Begehkarten_Vorlage.xlsx" : "Tankkarten_Vorlage.xlsx"}
              label={`Bulk-Import (${typeMeta.label})`}
              testid={`bulk-import-cards-${type}`}
              onDone={load}
            />
          )}
          {canCreate && (
            <button
              data-testid="card-new-btn"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-md"
            >
              <Plus className="w-4 h-4" /> Neue {typeMeta.singular}
            </button>
          )}
        </div>
      </div>

      {/* Type tabs */}
      <HelpHint title="Wie funktioniert die Karten-Verwaltung?" className="mb-4" testid="help-karten">
        <p>Hier verwaltest du <b>Begehkarten</b> (Werks-/Lager-Zugänge) und <b>Tankkarten</b> mit kompletter Zuweisungs-Historie pro Mitarbeiter und Fahrzeug.</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><b>Status-Workflow</b>: <i>Disponieren → Zugewiesen → Defekt/Verloren → Archiviert</i>. Jeder Wechsel landet im Audit-Log + in der Karten-Historie.</li>
          <li><b>Karte zuweisen</b> (Symbol User+): Mitarbeiter wählen, Übergabe-Datum + optionaler PIN/Code → wird automatisch im Mitarbeiter-Profil und im Fahrzeug-Profil verknüpft.</li>
          <li><b>Begehkarten</b> sind oft personen-gebunden, <b>Tankkarten</b> oft fahrzeug-gebunden — beide Typen werden hier in Tabs getrennt verwaltet.</li>
          <li><b>Massenaktion</b> erscheint bei Markierung — Bulk-Archivierung, Status-Wechsel oder Bulk-Löschung.</li>
          <li>Verlorene Karten <b>NIE löschen</b>, sondern auf <i>verloren</i> setzen — wichtig für Versicherungsmeldungen.</li>
        </ul>
      </HelpHint>

      <div className="flex gap-1 mb-5 border-b border-zinc-800" data-testid="card-type-tabs">
        {Object.entries(TYPE_META).map(([k, m]) => {
          const Icon = m.Icon;
          const active = type === k;
          return (
            <button
              key={k}
              onClick={() => setType(k)}
              data-testid={`card-tab-${k}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active ? "border-amber-500 text-zinc-50" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {m.label}
              <span className="ml-1 text-[10px] text-zinc-500 font-mono">
                {cards.filter((c) => c.type === k).length || ""}
              </span>
            </button>
          );
        })}
      </div>

      <BulkActionBar
        count={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={() => canDelete && setConfirmState({ type: "bulk", count: bulk.selectedCount })}
        deleteLabel={`${bulk.selectedCount} löschen`}
        testid="karten-bulk-bar"
      />

      <DataTable
        rows={cards}
        loading={loading}
        testid={`cards-table-${type}`}
        filters={<FirmFilter compact />}
        searchableFields={[
          "card_number", "label", "issuer", "notes",
          (c) => empName(c.assigned_to_employee_id),
          (c) => STATUS_META[c.status]?.label || "",
        ]}
        searchPlaceholder="Nummer, Bezeichnung, Mitarbeiter…"
        emptyTitle={`Keine ${typeMeta.label}`}
        emptyHint={`Legen Sie die erste ${typeMeta.singular} an.`}
        columns={[
          {
            key: "_select",
            label: <BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected} onChange={bulk.toggleAll} disabled={!canDelete} testid="karten-select-all" />,
            headerClassName: "w-8",
            className: "w-8",
            render: (c) => (
              <BulkCheckbox checked={bulk.isSelected(c.id)} onChange={() => bulk.toggle(c.id)} disabled={!canDelete} testid={`karten-select-${c.id}`} />
            ),
          },
          {
            key: "type", label: "", headerClassName: "w-6",
            render: () => <TypeIcon className="w-4 h-4 text-amber-400" />,
          },
          { key: "card_number", label: "Nummer", sortable: true,
            render: (c) => <span className="font-mono text-zinc-100">{c.card_number}</span> },
          { key: "label", label: "Bezeichnung",
            render: (c) => <span className="text-zinc-300">{c.label || "—"}</span> },
          ...(type === "tank" ? [
            { key: "issuer", label: "Anbieter",
              render: (c) => <span className="text-zinc-400 text-xs">{c.issuer || "—"}</span> },
            { key: "expiry_date", label: "Gültig bis", sortable: true,
              render: (c) => <span className="font-mono text-xs text-zinc-400">{fmtDate(c.expiry_date)}</span> },
          ] : []),
          { key: "status", label: "Status", sortable: true,
            render: (c) => <StatusBadge status={c.status} /> },
          { key: "_holder", label: "Aktuell bei",
            render: (c) => (
              <span className="text-zinc-200 text-sm">
                {c.status === "assigned" && c.assigned_to_employee_id
                  ? empName(c.assigned_to_employee_id)
                  : <span className="text-zinc-500 italic">{STATUS_META[c.status]?.label || "—"}</span>}
              </span>
            ) },
          {
            key: "_actions", label: "",
            headerClassName: "text-right", className: "text-right",
            render: (c) => (
              <div className="inline-flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); setHistoryModal(c); }}
                  data-testid={`card-history-${c.id}`}
                  title="Verlauf anzeigen"
                  className="p-1.5 text-zinc-400 hover:text-amber-300"
                ><History className="w-3.5 h-3.5" /></button>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setAssignModal(c); }}
                    data-testid={`card-assign-${c.id}`}
                    title="Zuweisen / Status ändern"
                    className="p-1.5 text-zinc-400 hover:text-blue-400"
                  ><UserPlus className="w-3.5 h-3.5" /></button>
                )}
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditing(c); setShowForm(true); }}
                    data-testid={`card-edit-${c.id}`}
                    title="Bearbeiten"
                    className="p-1.5 text-zinc-400 hover:text-zinc-100"
                  ><Pencil className="w-3.5 h-3.5" /></button>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(c); }}
                    data-testid={`card-del-${c.id}`}
                    title="Löschen"
                    className="p-1.5 text-zinc-400 hover:text-red-400"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ),
          },
        ]}
      />

      {showForm && (
        <CardFormModal
          type={type}
          card={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {assignModal && (
        <AssignModal
          card={assignModal}
          employees={employees}
          onClose={() => setAssignModal(null)}
          onSaved={() => { setAssignModal(null); load(); }}
        />
      )}

      {historyModal && (
        <HistoryModal
          card={historyModal}
          onClose={() => setHistoryModal(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.type === "bulk" ? "Mehrere Karten löschen?" : "Karte löschen?"}
        message={confirmState?.type === "bulk"
          ? `${confirmState?.count} Karten werden unwiderruflich gelöscht. Historie wird mitgelöscht. Fortfahren?`
          : `Karte „${confirmState?.card?.card_number}" wirklich löschen? Historie wird mitgelöscht.`}
        confirmLabel={confirmState?.type === "bulk" ? `Ja, ${confirmState?.count} löschen` : "Ja, löschen"}
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmRemove}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        testid="karten-confirm-delete"
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Create / Edit form
// ────────────────────────────────────────────────────────────
function CardFormModal({ type, card, onClose, onSaved }) {
  const editing = !!card;
  const [form, setForm] = useState({
    type: card?.type || type,
    card_number: card?.card_number || "",
    label: card?.label || "",
    issuer: card?.issuer || "",
    pin: card?.pin || "",
    expiry_date: card?.expiry_date || "",
    notes: card?.notes || "",
    department_id: card?.department_id || "",
    firm_id: card?.firm_id || "",
  });
  const [depts, setDepts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/departments").catch(() => ({ data: [] })),
      api.get("/firms").catch(() => ({ data: [] })),
    ]).then(([d, f]) => { setDepts(d.data || []); setFirms(f.data || []); });
  }, []);

  async function save() {
    const num = (form.card_number || "").trim();
    if (!num) { toast.error("Kartennummer erforderlich"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/cards/${card.id}`, form);
      } else {
        await api.post("/cards", form);
      }
      toast.success(editing ? "Karte aktualisiert" : "Karte angelegt");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  const isTank = form.type === "tank";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="card-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-50">
            {editing ? `${TYPE_META[form.type].singular} bearbeiten` : `Neue ${TYPE_META[form.type].singular}`}
          </h3>
          <button onClick={onClose} className="text-zinc-400" data-testid="card-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Kartennummer *</label>
            <input
              value={form.card_number}
              onChange={(e) => setForm({ ...form, card_number: e.target.value })}
              data-testid="card-form-number"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1 font-mono"
              placeholder={isTank ? "z. B. DKV-7891234" : "z. B. BK-001"}
            />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Bezeichnung</label>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="card-form-label"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
              placeholder={isTank ? "z. B. Diesel-Flottenkarte" : "z. B. Lager Wien Liesing"}
            />
          </div>
          {isTank && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Anbieter</label>
                <input
                  value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                  data-testid="card-form-issuer"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
                  placeholder="z. B. DKV, OMV, Shell…"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Gültig bis</label>
                <input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                  data-testid="card-form-expiry"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">PIN (optional)</label>
                <input
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value })}
                  data-testid="card-form-pin"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1 font-mono"
                  placeholder="nur wenn benötigt"
                />
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Abteilung</label>
            <select
              value={form.department_id || ""}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              data-testid="card-form-dept"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
            >
              <option value="">— keine —</option>
              {depts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Firma</label>
            <select
              value={form.firm_id || ""}
              onChange={(e) => setForm({ ...form, firm_id: e.target.value })}
              data-testid="card-form-firm"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
            >
              <option value="">— Auto (aus Abteilung) —</option>
              {firms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Notizen</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="card-form-notes"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
            />
          </div>
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="card-form-save"
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 text-white rounded font-medium"
          >
            {saving ? "Speichere…" : editing ? "Aktualisieren" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Assign / change-status modal
// ────────────────────────────────────────────────────────────
function AssignModal({ card, employees, onClose, onSaved }) {
  const [status, setStatus] = useState(card.status === "assigned" ? "assigned" : "assigned");
  const [employeeId, setEmployeeId] = useState(card.assigned_to_employee_id || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (status === "assigned" && !employeeId) {
      toast.error("Bitte Mitarbeiter auswählen");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cards/${card.id}/assign`, {
        status,
        employee_id: status === "assigned" ? employeeId : null,
        note,
      });
      toast.success("Status aktualisiert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktualisieren fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="card-assign-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-zinc-50">Karte zuweisen / Status ändern</h3>
            <p className="text-xs text-zinc-500 mt-0.5 font-mono">{card.card_number} · {card.label || "—"}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400" data-testid="card-assign-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Neuer Status</label>
            <div className="mt-2 grid grid-cols-1 gap-1.5">
              {Object.entries(STATUS_META).map(([k, m]) => {
                const Icon = m.Icon;
                const active = status === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setStatus(k)}
                    data-testid={`card-assign-status-${k}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded text-sm border text-left transition-colors ${
                      active ? m.color + " border-current" : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:bg-zinc-900"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {m.label}
                    {active && <span className="ml-auto text-[10px] font-bold">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
          {status === "assigned" && (
            <div data-testid="card-assign-emp-wrap">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Mitarbeiter *</label>
              <div className="mt-1">
                <EmployeePicker
                  value={employeeId}
                  onChange={setEmployeeId}
                  testid="card-assign-emp"
                  placeholder="— Mitarbeiter aus Liste wählen —"
                />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1 italic">Suchbar — Tippfehler werden verhindert.</p>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Notiz (optional)</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="card-assign-note"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"
              placeholder={`z. B. „Ausgabe am Montag zur Tour München“`}
            />
          </div>
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="card-assign-save"
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 text-white rounded font-medium"
          >
            {saving ? "Speichere…" : "Übernehmen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// History drawer
// ────────────────────────────────────────────────────────────
function HistoryModal({ card, onClose }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    api.get(`/cards/${card.id}/history`).then(({ data }) => setEntries(data));
  }, [card.id]);

  const rendered = useMemo(() => (entries || []).map((h) => {
    const ts = fmtDateTime(h.changed_at);
    return { ...h, _ts: ts };
  }), [entries]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="card-history-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-zinc-50">Verlauf</h3>
            <p className="text-xs text-zinc-500 mt-0.5 font-mono">{card.card_number} · {card.label || "—"}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400" data-testid="card-history-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {entries === null ? (
            <div className="text-center text-zinc-500 text-sm py-8">Lade…</div>
          ) : rendered.length === 0 ? (
            <EmptyState label="Keine Einträge" />
          ) : (
            <ol className="relative border-l-2 border-zinc-800 ml-2 space-y-5">
              {rendered.map((h, i) => {
                const meta = STATUS_META[h.to_status] || STATUS_META.dispo;
                const Icon = meta.Icon;
                return (
                  <li key={h.id || i} className="ml-4" data-testid={`card-history-entry-${i}`}>
                    <div className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-zinc-900 ${meta.color.split(' ')[0]}`} />
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm font-medium text-zinc-100">
                        {h.from_status ? `${STATUS_META[h.from_status]?.label || h.from_status} → ` : ""}
                        {meta.label}
                      </span>
                    </div>
                    {h.to_employee_name && (
                      <div className="text-xs text-blue-300">→ Mitarbeiter: <strong>{h.to_employee_name}</strong></div>
                    )}
                    {h.from_employee_name && !h.to_employee_name && (
                      <div className="text-xs text-zinc-400">Von: {h.from_employee_name}</div>
                    )}
                    {h.from_employee_name && h.to_employee_name && h.from_employee_id !== h.to_employee_id && (
                      <div className="text-xs text-zinc-500">(vorher: {h.from_employee_name})</div>
                    )}
                    {h.note && <div className="text-xs text-zinc-400 mt-1 italic">„{h.note}“</div>}
                    <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                      {h._ts} · {h.changed_by_user_name}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
